/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Mic, Sparkles, Save, FileText, RefreshCw, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import VoiceRecorder from '@/components/voicediary/VoiceRecorder';
import StructuredReview from '@/components/voicediary/StructuredReview';
import DiaryNoteCard from '@/components/voicediary/DiaryNoteCard';
import PatientPicker from '@/components/voicediary/PatientPicker';
import { VitalsPanel } from '@/components/voicediary/VitalsPanel';
import PatientTimelineDrawer from '@/components/patient/PatientTimelineDrawer';
import { OfflineStatusBadge } from '@/components/clinical/OfflineStatusBadge';
import { getVoiceDiarySchema, parseTranscript, listNotes, deleteNote } from '@/lib/voiceDiary';
import { saveVoiceNoteOffline, listVoiceNotesOffline, deleteRecord, STORES, setMeta, getMeta } from '@/lib/offlineDb';
import { syncEngine } from '@/lib/syncEngine';
import { SCHEMA_CACHE_KEY, emptyFromSchema, hasContent, offlineStructure } from '@/components/voicediary/diaryHelpers';
import { StepHeader } from '@/components/voicediary/StepHeader';

const VoiceDiaryPage = () => {
  const { user } = useAuth();
  const isPatient = user?.category === 'paziente';

  const [schema, setSchema] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [maxChars, setMaxChars] = useState(12000);
  const [transcript, setTranscript] = useState('');
  const [structured, setStructured] = useState({});
  const [patient, setPatient] = useState({ id: null, label: '' });
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [timelinePatient, setTimelinePatient] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [aiSource, setAiSource] = useState(null);

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true);
    try {
      const s = await getVoiceDiarySchema();
      setSchema(s.fields || []);
      setMaxChars(s.max_transcript_chars || 12000);
      setStructured((prev) => (hasContent(prev) ? prev : emptyFromSchema(s.fields || [])));
      setMeta(SCHEMA_CACHE_KEY, s).catch(() => {});
    } catch (e) {
      const cached = await getMeta(SCHEMA_CACHE_KEY).catch(() => null);
      if (cached?.fields) {
        setSchema(cached.fields);
        setMaxChars(cached.max_transcript_chars || 12000);
        setStructured((prev) => (hasContent(prev) ? prev : emptyFromSchema(cached.fields)));
      } else {
        toast.error(e?.response?.data?.detail || 'Diario disponibile solo per OSS/Infermieri/Medici');
      }
    } finally {
      setSchemaLoading(false);
    }
  }, []);

  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const [remote, local] = await Promise.all([
        listNotes({ limit: 20 }).then((r) => r.items || []).catch(() => []),
        listVoiceNotesOffline().catch(() => []),
      ]);
      const pending = local.filter((l) => l.sync_status !== 'synced').map((l) => ({ ...l, _local: true }));
      setNotes([...pending, ...remote]);
    } finally {
      setNotesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPatient) return;
    loadSchema();
    loadNotes();
  }, [isPatient, loadSchema, loadNotes]);

  const runParse = async () => {
    if (!transcript.trim()) { toast.error('Registra o digita prima una trascrizione'); return; }
    setBusy(true);
    try {
      const res = await parseTranscript(transcript);
      setStructured(res.structured || emptyFromSchema(schema));
      setAiSource('server');
      toast.success('Nota strutturata pronta per la revisione');
    } catch (e) {
      // Offline / errore → motore locale deterministico, mai bloccare l'OSS.
      setStructured(offlineStructure(transcript, schema));
      setAiSource('local');
      toast.info('AI remota non disponibile: nota strutturata con motore locale');
    } finally {
      setBusy(false);
      setReviewOpen(true);
    }
  };

  const runSave = async () => {
    if (!transcript.trim()) { toast.error('Trascrizione mancante'); return; }
    setSaving(true);
    try {
      const tagArr = tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 10);
      const created = await saveVoiceNoteOffline({
        transcript,
        structured,
        patient_id: patient.id || null,
        patient_label: patient.label?.trim() || null,
        tags: tagArr,
        author_id: user?.id || null,
      });
      toast.success(navigator.onLine ? 'Nota salvata · sincronizzazione in corso' : 'Nota salvata offline · sync al ritorno della rete');
      setNotes((prev) => [{ ...created, _local: true }, ...prev]);
      setTranscript('');
      setStructured(emptyFromSchema(schema));
      setPatient({ id: null, label: '' });
      setTags('');
      setAiSource(null);
      setReviewOpen(false);
      if (navigator.vibrate) navigator.vibrate(40);
      syncEngine.flush().then(loadNotes);
    } catch (e) {
      toast.error('Errore nel salvataggio locale');
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async (note) => {
    if (!window.confirm(`Eliminare la nota${note.patient_label ? ` di ${note.patient_label}` : ''}?`)) return;
    try {
      if (note._local) await deleteRecord(STORES.VOICE_NOTES, note.id);
      else await deleteNote(note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      toast.success('Nota eliminata');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const structuredReady = useMemo(() => hasContent(structured), [structured]);

  if (isPatient) {
    return (
      <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Il Diario Vocale è riservato agli operatori sanitari.</CardContent></Card>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl pb-28 lg:pb-6" data-testid="voice-diary-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Mic className="h-6 w-6 text-primary" /> Diario Vocale
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Parla, l'AI struttura la nota. Funziona anche offline.</p>
        </div>
        <OfflineStatusBadge />
      </div>

      {/* Step 1 — Registra */}
      <Card className="rounded-xl" data-testid="voice-step-record">
        <CardContent className="p-4 space-y-4">
          <StepHeader n={1} icon={Mic} title="Registra" subtitle="Riconoscimento vocale italiano on-device, gratuito" done={!!transcript.trim()} />
          <VoiceRecorder value={transcript} onChange={setTranscript} maxChars={maxChars} disabled={busy || saving} />
        </CardContent>
      </Card>

      {/* Step 2 — Struttura */}
      <Card className="rounded-xl" data-testid="voice-step-structure">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <StepHeader n={2} icon={Sparkles} title="Struttura con AI" subtitle="Compila automaticamente i campi della nota" done={structuredReady} />
            <Button onClick={runParse} disabled={busy || saving || !transcript.trim()} className="touch-target" data-testid="voice-parse-btn">
              {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy ? 'Elaborazione…' : 'Estrai nota'}
            </Button>
          </div>
          {aiSource && (
            <Badge variant="outline" className="text-[10px]" data-testid="voice-ai-source">{aiSource === 'local' ? 'Motore locale (offline)' : 'AI clinica'}</Badge>
          )}

          {busy ? (
            <div className="space-y-2" data-testid="voice-structure-skeleton">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-11 rounded-xl" />)}
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setReviewOpen((v) => !v)}
                className="w-full flex items-center justify-between rounded-xl border border-border/70 bg-muted/40 px-3 py-3 text-sm font-medium touch-target hover:border-primary/40 transition-colors"
                data-testid="voice-review-toggle"
                aria-expanded={reviewOpen}
              >
                <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Rivedi e correggi i campi {structuredReady && <Badge variant="success" className="text-[10px]">compilata</Badge>}</span>
                {reviewOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              {reviewOpen && (
                <div className="space-y-4 animate-fade-slide">
                  {schemaLoading || schema.length === 0 ? (
                    <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-11 rounded-xl" />)}</div>
                  ) : (
                    <StructuredReview schema={schema} value={structured} onChange={setStructured} disabled={busy || saving} />
                  )}
                  <div className="grid gap-3 sm:grid-cols-2 pt-3 border-t border-border">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Paziente (cerca o digita libero)</label>
                      <PatientPicker endpoint="/voice-diary/patients" value={patient} onChange={setPatient} placeholder="es. Sig.ra Rossi" testIdPrefix="voice-patient-picker" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Tag (separati da virgola)</label>
                      <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="es. domiciliare, mattina" maxLength={200} className="h-11 rounded-xl" data-testid="tags-input" />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — Salva (desktop inline) */}
      <div className="hidden lg:flex justify-end">
        <Button onClick={runSave} disabled={saving || busy || !transcript.trim()} className="touch-target min-w-[180px]" data-testid="voice-save-btn">
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva nota
        </Button>
      </div>

      {/* Parametri vitali — local-first (IndexedDB → sync al ritorno della rete) */}
      <VitalsPanel patient={patient} />

      {/* Cronologia */}
      <div data-testid="voice-history">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="h-4 w-4 text-primary" />
          <h2 className="font-display font-semibold">Cronologia note ({notes.length})</h2>
          <Button variant="ghost" size="sm" onClick={loadNotes} disabled={notesLoading} className="ml-auto touch-target" data-testid="reload-notes-btn">
            <RefreshCw className={cn('h-3.5 w-3.5', notesLoading && 'animate-spin')} /> Aggiorna
          </Button>
        </div>
        <div className="space-y-2">
          {notesLoading && [...Array(3)].map((_, i) => <Skeleton key={`voice-diary-skel-${i}`} className="h-24 rounded-xl" />)}
          {!notesLoading && notes.length === 0 && (
            <Card className="rounded-xl"><CardContent className="p-6 text-center text-sm text-muted-foreground">Nessuna nota salvata. Registra la tua prima nota qui sopra.</CardContent></Card>
          )}
          {!notesLoading && notes.map((n) => (
            <div key={n.id} className="relative">
              {n._local && <Badge variant="outline" className="absolute right-3 top-3 z-10 text-[9px] border-warning/50 text-warning bg-card">in attesa di sync</Badge>}
              <DiaryNoteCard note={n} onDelete={runDelete} onOpenTimeline={setTimelinePatient} schema={schema} />
            </div>
          ))}
        </div>
      </div>

      {/* Sticky mobile action bar */}
      <div className="lg:hidden fixed inset-x-0 bottom-[64px] z-[150] px-3" data-testid="voice-mobile-actionbar">
        <div className="glass-strong rounded-xl p-2 flex items-center gap-2 shadow-elev-md">
          <Button variant="outline" onClick={runParse} disabled={busy || saving || !transcript.trim()} className="flex-1 h-12 rounded-xl" data-testid="voice-parse-btn-mobile">
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Struttura
          </Button>
          <Button onClick={runSave} disabled={saving || busy || !transcript.trim()} className="flex-1 h-12 rounded-xl" data-testid="voice-save-btn-mobile">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </Button>
        </div>
      </div>

      <PatientTimelineDrawer
        patientId={timelinePatient?.id || null}
        patientLabel={timelinePatient?.label || ''}
        open={!!timelinePatient}
        onOpenChange={(v) => { if (!v) setTimelinePatient(null); }}
      />
    </div>
  );
};

export default VoiceDiaryPage;
